/**
 * Observability and Trace Commands
 * Provides debugging and tracing capabilities for the AI agent
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { existsSync, readFileSync, mkdirSync, writeFileSync, readdirSync, unlinkSync, rmSync } from 'fs';
import { join } from 'path';
import { format } from 'util';
import { getConfigDir } from '../utils/config.js';

export interface TraceRecord {
  id: string;
  timestamp: Date;
  command: string;
  input: string;
  llmRequest: {
    prompt: string;
    context: any;
    tools: string[];
  };
  llmResponse: {
    content: string;
    toolCalls: any[];
    duration: number;
    tokens?: {
      input: number;
      output: number;
    };
  };
  execution: {
    steps: ExecutionStep[];
    success: boolean;
    errors?: string[];
  };
  environment: {
    nodeVersion: string;
    platform: string;
    cwd: string;
    env: Record<string, string>;
  };
}

export interface ExecutionStep {
  id: string;
  action: string;
  duration: number;
  result: any;
  error?: string;
}

class TraceManager {
  private traceDir: string;
  private maxRecords: number;

  constructor() {
    this.traceDir = join(getConfigDir(), 'traces');
    this.maxRecords = 50;
    this.ensureTraceDir();
  }

  private ensureTraceDir(): void {
    if (!existsSync(this.traceDir)) {
      mkdirSync(this.traceDir, { recursive: true });
    }
  }

  addRecord(record: TraceRecord): void {
    const filename = `${record.id}.json`;
    const filepath = join(this.traceDir, filename);
    
    try {
      writeFileSync(filepath, JSON.stringify(record, null, 2));
      this.cleanupOldRecords();
    } catch (error) {
      console.error(chalk.red(`Failed to save trace: ${error}`));
    }
  }

  getLatestRecord(): TraceRecord | null {
    const files = this.listTraceFiles();
    if (files.length === 0) return null;

    const latest = files[files.length - 1];
    const filepath = join(this.traceDir, latest);
    
    try {
      const content = readFileSync(filepath, 'utf-8');
      return JSON.parse(content) as TraceRecord;
    } catch {
      return null;
    }
  }

  listTraceFiles(): string[] {
    try {
      if (!existsSync(this.traceDir)) return [];
      const files = readdirSync(this.traceDir).filter(f => f.endsWith('.json'));
      // Sort by modification time (newest last) using stat
      return files.sort((a, b) => {
        try {
          const statA = readFileSync(join(this.traceDir, a), 'utf-8');
          const statB = readFileSync(join(this.traceDir, b), 'utf-8');
          const tA = JSON.parse(statA).timestamp || a;
          const tB = JSON.parse(statB).timestamp || b;
          return String(tA).localeCompare(String(tB));
        } catch {
          return a.localeCompare(b);
        }
      });
    } catch {
      return [];
    }
  }

  private cleanupOldRecords(): void {
    const files = this.listTraceFiles();
    if (files.length > this.maxRecords) {
      const toDelete = files.slice(0, files.length - this.maxRecords);
      for (const file of toDelete) {
        try {
          unlinkSync(join(this.traceDir, file));
        } catch {
          // Best-effort cleanup
        }
      }
    }
  }

  generateReport(): void {
    const files = this.listTraceFiles();
    if (files.length === 0) {
      console.log(chalk.yellow('No trace records found.'));
      return;
    }

    console.log(chalk.bold.blue('\n📊 Trace Report\n'));
    
    let totalDuration = 0;
    let successCount = 0;
    let errorCount = 0;

    files.forEach((file, index) => {
      try {
        const filepath = join(this.traceDir, file);
        const content = readFileSync(filepath, 'utf-8');
        const record: TraceRecord = JSON.parse(content);
        // Normalize timestamp from JSON (may be string or Date)
        const ts = record.timestamp instanceof Date ? record.timestamp : new Date(record.timestamp as unknown as string);
        
        console.log(chalk.dim(`\n${index + 1}. ${ts.toISOString()}`));
        console.log(chalk.cyan(`   Command: ${record.command}`));
        console.log(chalk.dim(`   Input: ${record.input.substring(0, 100)}${record.input.length > 100 ? '...' : ''}`));
        console.log(chalk.green(`   Duration: ${record.llmResponse.duration}ms`));
        
        if (record.execution.success) {
          successCount++;
          console.log(chalk.green('   Status: ✅ Success'));
        } else {
          errorCount++;
          console.log(chalk.red('   Status: ❌ Failed'));
          if (record.execution.errors) {
            record.execution.errors.forEach(error => {
              console.log(chalk.red(`     Error: ${error}`));
            });
          }
        }
        
        totalDuration += record.llmResponse.duration;
      } catch {
        console.log(chalk.red(`   Failed to parse ${file}`));
      }
    });

    console.log(chalk.bold.blue('\n📈 Summary'));
    console.log(`Total traces: ${files.length}`);
    console.log(`Success rate: ${((successCount / files.length) * 100).toFixed(1)}%`);
    console.log(`Average duration: ${Math.round(totalDuration / files.length)}ms`);
  }
}

const traceManager = new TraceManager();

export const traceCommand = new Command('trace')
  .description('View and analyze agent execution traces')
  .option('-c, --clear', 'Clear all trace records')
  .option('-r, --report', 'Generate summary report')
  .option('-l, --latest', 'Show latest trace record')
  .option('--show <id>', 'Show specific trace record by ID')
  .action((options) => {
    if (options.clear) {
      try {
        const tracePath = join(getConfigDir(), 'traces');
        if (existsSync(tracePath)) {
          rmSync(tracePath, { recursive: true, force: true });
        }
        console.log(chalk.green('✅ Trace records cleared'));
      } catch (error) {
        console.error(chalk.red(`Failed to clear traces: ${error}`));
      }
      return;
    }

    if (options.report) {
      traceManager.generateReport();
      return;
    }

    if (options.latest) {
      const latest = traceManager.getLatestRecord();
      if (!latest) {
        console.log(chalk.yellow('No trace records found.'));
        return;
      }

      console.log(chalk.bold.blue(`\n🔍 Latest Trace Record (${latest.id})\n`));
      const latestTs = latest.timestamp instanceof Date ? latest.timestamp : new Date(latest.timestamp as unknown as string);
      console.log(chalk.dim(`Timestamp: ${latestTs.toISOString()}`));
      console.log(chalk.cyan(`Command: ${latest.command}`));
      console.log(chalk.dim(`Input: ${latest.input}\n`));
      
      if (latest.llmResponse.tokens) {
        console.log(chalk.blue('📊 Token Usage:'));
        console.log(`  Input tokens: ${latest.llmResponse.tokens.input}`);
        console.log(`  Output tokens: ${latest.llmResponse.tokens.output}`);
        console.log(`  Total: ${latest.llmResponse.tokens.input + latest.llmResponse.tokens.output}\n`);
      }

      if (latest.llmResponse.toolCalls.length > 0) {
        console.log(chalk.blue('🔧 Tool Calls:'));
        latest.llmResponse.toolCalls.forEach((tool: any, index: number) => {
          console.log(`  ${index + 1}. ${tool.name}`);
          if (tool.arguments) {
            console.log(chalk.dim(`     Args: ${JSON.stringify(tool.arguments, null, 2)}`));
          }
        });
        console.log('');
      }

      if (latest.execution.steps.length > 0) {
        console.log(chalk.blue('⚡ Execution Steps:'));
        latest.execution.steps.forEach((step: ExecutionStep, index: number) => {
          const icon = step.error ? '❌' : '✅';
          console.log(`  ${index + 1}. ${step.action} ${icon} (${step.duration}ms)`);
          if (step.error) {
            console.log(chalk.red(`     Error: ${step.error}`));
          }
        });
      }

      return;
    }

    if (options.show) {
      try {
        const filepath = join(getConfigDir(), 'traces', `${options.show}.json`);
        if (!existsSync(filepath)) {
          console.log(chalk.red(`Trace record "${options.show}" not found.`));
          return;
        }

        const content = readFileSync(filepath, 'utf-8');
        const record: TraceRecord = JSON.parse(content);
        
        console.log(chalk.bold.blue(`\n🔍 Trace Record: ${record.id}\n`));
        console.log(format(JSON.stringify(record, null, 2)));
      } catch (error) {
        console.error(chalk.red(`Failed to load trace record: ${error}`));
      }
      return;
    }

    console.log(chalk.bold.blue('\n📋 Available Trace Records\n'));
    const files = traceManager.listTraceFiles();
    if (files.length === 0) {
      console.log(chalk.yellow('No trace records found.'));
      console.log(chalk.dim('Run agent commands to generate traces.'));
      return;
    }

    files.forEach((file, index) => {
      const id = file.replace('.json', '');
      console.log(`${index + 1}. ${id}`);
    });
    
    console.log(chalk.dim('\nUse --show <id> to view details, --report for summary, or --clear to remove all.'));
  });

export function createTraceRecord(command: string, input: string): TraceRecord {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    command,
    input,
    llmRequest: {
      prompt: '',
      context: {},
      tools: []
    },
    llmResponse: {
      content: '',
      toolCalls: [],
      duration: 0,
      tokens: {
        input: 0,
        output: 0
      }
    },
    execution: {
      steps: [],
      success: true
    },
    environment: {
      nodeVersion: process.version,
      platform: process.platform,
      cwd: process.cwd(),
      env: {
        TERM: process.env.TERM || '',
        SHELL: process.env.SHELL || '',
        PATH: process.env.PATH ? process.env.PATH.split(':').slice(0, 3).join(':') + '...' : ''
      }
    }
  };
}

export { traceManager };